/**
 * Mocked tournament for the Tournaments explore page. Single-elim
 * 8-team bracket, mid-tournament state:
 *   - Round 0 (quarterfinals): all 4 complete
 *   - Round 1 (semifinals): 1 complete, 1 in-progress
 *   - Round 2 (grand final): TBD, awaiting semifinal winner
 *
 * Picked the mid-state on purpose — it lets the bracket mockup show
 * winner highlights AND empty TBD slots in one frame.
 *
 * Realistic prize-pool numbers: £80 pot at £5 entry, 16 entrants =
 * £80 (just enough for one tier of paid tournament without being
 * unbelievable).
 */

export type MockMatch = {
  matchId: string
  round: number
  matchIndex: number
  participantA: MockTeam | null
  participantB: MockTeam | null
  winnerTeamId: string | null
  status: 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETE' | 'AWAITING_CONFIRMATION'
  kind: 'W' | 'GF'
  bestOf: number
  scheduledAt: string | null
}

export type MockTeam = {
  teamId: string
  teamLabel: string
  avatarUrl: string
  memberCallsign: string
}

// Each team has its own hand-designed SVG mark living in
// /public/team-logos/. Distinct geometric symbols + accent colour
// per team — sells "this is a real org with a real logo" much better
// than dicebear's auto-generated initials. See the docstring at the
// top of each SVG file for the design language: hex shield → unique
// emblem → team name set in Space Grotesk.
const T = (label: string, slug: string): MockTeam => ({
  teamId: `team-${slug}`,
  teamLabel: label,
  avatarUrl: `/team-logos/${slug}.svg`,
  memberCallsign: `@${slug}`,
})

const ORION       = T('Orion', 'orion')
const STORMBREAKER = T('Stormbreaker', 'stormbreaker')
const PALADIN     = T('Paladin', 'paladin')
const SABER       = T('Saber', 'saber')
const TEMPEST     = T('Tempest', 'tempest')
const CYPHER      = T('Cypher', 'cypher')
const JUNO        = T('Juno', 'juno')
const ROOK        = T('Rook', 'rook')

export const MOCK_TOURNAMENT_TEAMS = {
  ORION, STORMBREAKER, PALADIN, SABER, TEMPEST, CYPHER, JUNO, ROOK,
} as const

const NOW_ISO = '2026-06-16T20:00:00Z'
const HOURS_FROM_NOW = (h: number) =>
  new Date(Date.parse(NOW_ISO) + h * 3_600_000).toISOString()

export const MOCK_TOURNAMENT = {
  id: 'mock-tournament-friday-cs2',
  title: 'Friday Bracket Night · CS2',
  gameName: 'Counter-Strike 2',
  format: 'Single Elim',
  bestOf: 1,
  entryFeePence: 500,
  prizePoolPence: 8_000,                  // £80 pot
  attendeeCount: 8,
  attendeeCap: 8,
  startsAt: '2026-06-16T19:30:00Z',       // 30 min ago — so it's IN_PROGRESS
}

/**
 * Bracket matches — mirrors the shape the real Stadium components
 * consume so the marketing mockups can copy the visual treatment
 * cleanly.
 *
 * State summary:
 *   QF1: Orion beat Saber
 *   QF2: Stormbreaker beat Cypher
 *   QF3: Paladin beat Juno
 *   QF4: Tempest beat Rook
 *   SF1: Orion vs Stormbreaker — IN PROGRESS
 *   SF2: Paladin beat Tempest
 *   GF:  TBD vs Paladin (awaiting SF1 winner)
 */
export const MOCK_BRACKET_MATCHES: MockMatch[] = [
  // ── Round 0 — Quarterfinals (all complete) ───────────────────────
  {
    matchId: 'qf1',
    round: 0,
    matchIndex: 0,
    participantA: ORION,
    participantB: SABER,
    winnerTeamId: ORION.teamId,
    status: 'COMPLETE',
    kind: 'W',
    bestOf: 1,
    scheduledAt: '2026-06-16T19:30:00Z',
  },
  {
    matchId: 'qf2',
    round: 0,
    matchIndex: 1,
    participantA: STORMBREAKER,
    participantB: CYPHER,
    winnerTeamId: STORMBREAKER.teamId,
    status: 'COMPLETE',
    kind: 'W',
    bestOf: 1,
    scheduledAt: '2026-06-16T19:30:00Z',
  },
  {
    matchId: 'qf3',
    round: 0,
    matchIndex: 2,
    participantA: PALADIN,
    participantB: JUNO,
    winnerTeamId: PALADIN.teamId,
    status: 'COMPLETE',
    kind: 'W',
    bestOf: 1,
    scheduledAt: '2026-06-16T19:30:00Z',
  },
  {
    matchId: 'qf4',
    round: 0,
    matchIndex: 3,
    participantA: TEMPEST,
    participantB: ROOK,
    winnerTeamId: TEMPEST.teamId,
    status: 'COMPLETE',
    kind: 'W',
    bestOf: 1,
    scheduledAt: '2026-06-16T19:30:00Z',
  },
  // ── Round 1 — Semifinals ─────────────────────────────────────────
  {
    matchId: 'sf1',
    round: 1,
    matchIndex: 0,
    participantA: ORION,
    participantB: STORMBREAKER,
    winnerTeamId: null,
    status: 'IN_PROGRESS',
    kind: 'W',
    bestOf: 1,
    scheduledAt: HOURS_FROM_NOW(0.1),
  },
  {
    matchId: 'sf2',
    round: 1,
    matchIndex: 1,
    participantA: PALADIN,
    participantB: TEMPEST,
    winnerTeamId: PALADIN.teamId,
    status: 'COMPLETE',
    kind: 'W',
    bestOf: 1,
    scheduledAt: '2026-06-16T20:00:00Z',
  },
  // ── Round 2 — Grand Final ────────────────────────────────────────
  {
    matchId: 'gf',
    round: 2,
    matchIndex: 0,
    participantA: null,             // SF1 winner pending
    participantB: PALADIN,
    winnerTeamId: null,
    status: 'SCHEDULED',
    kind: 'GF',
    bestOf: 1,
    scheduledAt: HOURS_FROM_NOW(1.5),
  },
]
